import { useTranslations } from '@vocab/react';
import { Alert, Text } from 'braid-design-system';
import { useEffect } from 'react';

import { useAnalytics } from 'src/contexts/AnalyticsContext';
import type {
  ErrorCode,
  ErrorMessage,
  ServerError as ServerErrorType,
} from 'src/types';

import translations from './.vocab';

interface Props {
  error: ServerErrorType;
  currentPageSection?: string;
}

const defaultErrorMessage = 'Something went wrong';

const mapping: Record<Exclude<ErrorCode | ErrorMessage, null>, string> = {
  "That's not your password": "That's not your password",
  'Oops!': 'Oops!',
  'This email address is currently in use. Please choose another email address.':
    'This email address is currently in use. Please choose another email address.',
  'Something went wrong': 'Something went wrong',
  code_expired: 'The code has expired.',
  wrong_code: 'Invalid code. Try again or click resend.',
  max_attempts:
    'You’ve reached the maximum number of attempts. Please click resend.',
  too_many_attempts:
    'Something’s not right. We’ve sent instructions on how to proceed.',
  rate_limited:
    'We have detected too many requests from your IP address. Please try again after ten minutes or contact customer service.',
};

function getErrorMessage(errorMessageKey: string): string | undefined {
  if (errorMessageKey in mapping) {
    return mapping[errorMessageKey as keyof typeof mapping];
  }
  return errorMessageKey;
}

const ServerError = ({ error, currentPageSection }: Props) => {
  const { t } = useTranslations(translations);
  const { trackAccountLink } = useAnalytics();
  const erroMessageKey =
    error.errorCode ?? error.errorMessage ?? defaultErrorMessage;

  useEffect(() => {
    if (currentPageSection) {
      trackAccountLink({
        eventName: 'alert_viewed',
        currentPageSection,
        authenticationFlow: 'changeEmail',
        alertShownType: 'alert',
        alertShownPosition: 'content',
        alertShownTitle: error.errorCode?.toString(),
        alertShownMessage: getErrorMessage(erroMessageKey),
      });
    }
  }, [error, erroMessageKey, trackAccountLink, currentPageSection]);

  return (
    <Alert tone="critical">
      <Text>{t(erroMessageKey) || t(defaultErrorMessage)}</Text>
    </Alert>
  );
};

export default ServerError;
